import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, CheckCircle2, Eye, EyeOff, UserCheck } from "lucide-react";

export default function JoinClub() {
  const { aid } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ first_name: "", last_name: "", email: sp.get("email") || "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/public/clubs/${aid}/join-info`).then((r) => setClub(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Club introuvable."));
  }, [aid]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum."); return; }
    setBusy(true);
    try {
      await api.post("/public/clubs/members/register", { ...f, club_id: aid });
      setDone(true);
      setTimeout(() => nav("/connexion"), 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  const joinAsUser = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/clubs/${aid}/join-as-user`);
      toast.success(data.already ? "Vous êtes déjà membre de ce club." : "Vous avez rejoint le club !");
      setAlready(!!data.already);
      setDone(true);
      setTimeout(() => nav("/compte"), 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="max-w-md mx-auto py-20 px-4 text-center"><h1 className="text-2xl font-bold text-rose-700">Erreur</h1><p className="mt-3 text-slate-600">{err}</p><Link to="/" className="mt-4 inline-block text-[#E05638]">← Accueil</Link></div>;
  if (!club) return <div className="max-w-md mx-auto py-20 px-4 text-center text-slate-500">Chargement...</div>;
  if (done) return (
    <div className="max-w-md mx-auto py-16 px-4 text-center" data-testid="join-club-done">
      <CheckCircle2 size={48} className="mx-auto text-indigo-600" />
      <h1 className="mt-4 font-heading text-2xl font-extrabold">Bienvenue !</h1>
      <p className="mt-2 text-slate-600">{already ? <>Vous étiez déjà membre de <b>{club.name}</b>.</> : <>Vous êtes maintenant membre de <b>{club.name}</b>.</>}</p>
    </div>
  );

  const Header = (
    <div className="flex items-center gap-3">
      {club.logo_url ? <img src={club.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <Users className="text-indigo-700" size={28} />}
      <div>
        <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Rejoindre</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold" data-testid="join-club-title">{club.name}</h1>
        <div className="text-xs text-slate-500">{club.category_label}{club.city ? ` · ${club.city}` : ""}</div>
      </div>
    </div>
  );

  // Utilisateur déjà connecté : parcours sans friction
  if (user) return (
    <div className="max-w-lg mx-auto py-10 px-4" data-testid="join-club-page-logged">
      {Header}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 text-center" data-testid="join-club-existing-card">
        <UserCheck size={40} className="mx-auto text-indigo-600" />
        <h2 className="mt-3 font-heading text-xl font-extrabold">Vous êtes déjà connecté</h2>
        <p className="mt-2 text-slate-600 text-sm">
          Rejoignez <b>{club.name}</b> en un clic avec votre compte <b>{user.email}</b>, sans recréer d'identifiants.
        </p>
        <Button onClick={joinAsUser} disabled={busy} className="btn-shine text-white w-full mt-5" data-testid="join-club-existing-btn">
          {busy ? "..." : `Rejoindre avec mon compte`}
        </Button>
        <Link to={`/clubs/${aid}`} className="mt-3 inline-block text-sm text-slate-500 hover:text-indigo-700">Voir la page du club</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto py-10 px-4" data-testid="join-club-page">
      {Header}
      <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm text-indigo-900">
        Créez votre compte gratuit PROXIreduc-28 pour suivre la vie du club et profiter des avantages locaux.
      </div>
      <p className="mt-2 text-sm text-slate-500">
        Vous avez déjà un compte ? <Link to="/connexion" className="text-indigo-700 font-semibold hover:underline" data-testid="join-club-login-link">Connectez-vous</Link> pour rejoindre en un clic.
      </p>
      <form onSubmit={submit} className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="join-club-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom *</Label><Input required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="join-club-firstname" /></div>
          <div><Label>Nom *</Label><Input required value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="join-club-lastname" /></div>
        </div>
        <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="join-club-email" /></div>
        <div>
          <Label>Mot de passe *</Label>
          <div className="mt-1 relative">
            <Input required type={showPwd ? "text" : "password"} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="pr-10" data-testid="join-club-password" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" aria-label={showPwd ? "Masquer" : "Afficher"} data-testid="join-club-password-toggle">
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <Button type="submit" disabled={busy} className="btn-shine text-white w-full" data-testid="join-club-submit">{busy ? "..." : `Rejoindre ${club.name}`}</Button>
        <p className="text-[11px] text-slate-500 text-center">En vous inscrivant, vous acceptez les CGU et la politique de confidentialité.</p>
      </form>
    </div>
  );
}
